/**
 * PaymentFormModal — R6/MP (Manual Payment)
 *
 * Erfassen UND Bearbeiten einer Zahlung in einer Komponente (mode: create|edit).
 * Feld-Paritaet zu v1 app/views/payments/_form.haml:1-18:
 *   Kontakt (Pflicht) | Betrag HNT | Datum/Zeit | Waehrung | Fremdbetrag |
 *   Zahlungsart | Memo
 *
 * Besonderheiten:
 *   * Die Liste liefert `amount_hnt` fuer ausgehende Zahlungen NEGATIV
 *     (reine Anzeige-Logik im Serializer). Das Edit-Prefill nutzt daher
 *     Math.abs(), sonst wird der Wert mit 422 invalid_amount abgelehnt.
 *   * Die Contact-Zuordnung ist ein normales Feld (ersetzt v1 select_contact);
 *     im Edit-Modus darf sie auch wieder entfernt werden.
 *   * Zukunftsdaten sind verboten (Backend: 422 invalid_time).
 *   * Bearbeitet werden koennen nur MANUELLE Zahlungen — importierte tragen
 *     gar keine Edit-Aktion mehr (PaymentRow), das Backend antwortet sonst mit
 *     403 imported_readonly.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useContacts } from '../contacts/useContacts'
import { useContactOption } from '../contacts/useContactOption'
import { toast } from '../../components/Toast/useToast'
import {
  useCreatePayment,
  useUpdatePayment,
  paymentErrorCode,
  PAYMENT_METHODS,
  CRYPTO_CURRENCIES,
  FIAT_CURRENCIES,
  KNOWN_CURRENCIES,
  type PaymentFormData,
} from './usePaymentMutations'
import type { Payment } from './usePayments'

type Props = {
  isOpen: boolean
  mode: 'create' | 'edit'
  /** Nur im Edit-Modus: die zu bearbeitende Zeile. */
  payment?: Payment | null
  /** Nur im Create-Modus: vorbelegter Kontakt (Einstieg von der Contact-Detailseite). */
  presetContactId?: number | null
  onClose: () => void
  onSaved?: () => void
}

type FormErrors = {
  contact?: string
  amount?: string
  time?: string
  currency?: string
}

/** Date → "YYYY-MM-DDTHH:mm" in LOKALER Zeit (datetime-local erwartet keine Zone). */
function toLocalInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  )
}

/**
 * Der Wrapper mountet das Formular erst beim Oeffnen (und remountet es per key,
 * wenn eine andere Zeile bearbeitet wird). Dadurch kommt das Prefill aus den
 * useState-Initializern statt aus einem setState-im-Effect (react-hooks/
 * set-state-in-effect) — kein Kaskaden-Render beim Oeffnen.
 */
export function PaymentFormModal(props: Props) {
  if (!props.isOpen) return null
  const key = props.mode === 'edit' ? `edit-${props.payment?.id ?? 'none'}` : `create-${props.presetContactId ?? 'none'}`
  return <PaymentForm key={key} {...props} />
}

function PaymentForm({ mode, payment, presetContactId, onClose, onSaved }: Props) {
  const { t } = useTranslation('payments')
  // Backend-Limit ist per_page=100.
  const { contacts } = useContacts({ perPage: 100 })

  const isEdit = mode === 'edit' && !!payment

  // Ausgangswerte einmal berechnen: sie speisen die useState-Initializer UND
  // dienen als Referenz fuer die Dirty-Erkennung (R6-Review F2).
  const [initialValues] = useState(() => ({
    contactId:
      isEdit && payment.contact_id != null
        ? String(payment.contact_id)
        : (!isEdit && presetContactId != null ? String(presetContactId) : ''),
    // Vorzeichen-Falle: amount_hnt ist bei ausgehenden Zahlungen negativ — sonst
    // wuerde der Edit-Roundtrip mit 422 invalid_amount abgelehnt.
    amount: isEdit ? String(Math.abs(payment.amount_hnt ?? 0)) : '',
    time: toLocalInputValue(isEdit && payment.created_at ? new Date(payment.created_at) : new Date()),
    // F-R6b-1: beim Erfassen KEINE Vorbelegung — der Nutzer muss die Waehrung
    // bewusst waehlen (Backend lehnt fehlende currency mit 422 invalid_currency ab).
    currency: isEdit ? (payment.currency ?? '') : '',
    currencyAmount: isEdit && payment.currency_amount != null ? String(payment.currency_amount) : '',
    paymentMethod: isEdit ? (payment.payment_method ?? 'hnt') : 'hnt',
    memo: isEdit ? (payment.memo ?? '') : '',
  }))

  const [contactId, setContactId] = useState(initialValues.contactId)
  const [amount, setAmount] = useState(initialValues.amount)
  const [time, setTime] = useState(initialValues.time)
  const [currency, setCurrency] = useState(initialValues.currency)
  const [currencyAmount, setCurrencyAmount] = useState(initialValues.currencyAmount)
  const [paymentMethod, setPaymentMethod] = useState<string>(initialValues.paymentMethod)
  const [memo, setMemo] = useState(initialValues.memo)
  const [errors, setErrors] = useState<FormErrors>({})
  // R6-Review F2: Nachfrage vor dem Verwerfen ausgefuellter Eingaben.
  const [confirmDiscard, setConfirmDiscard] = useState(false)

  const createPayment = useCreatePayment()
  const updatePayment = useUpdatePayment()
  const isPending = createPayment.isPending || updatePayment.isPending

  // --- R6-Review F5: zugeordneten Kontakt ausserhalb der ersten 100 nachladen ---
  const assignedContactId = isEdit ? payment.contact_id : null
  const contactsLoaded = contacts !== undefined
  const assignedContactMissing =
    contactsLoaded &&
    assignedContactId != null &&
    !(contacts ?? []).some((c) => c.id === assignedContactId)
  const fetchedContact = useContactOption(assignedContactId, assignedContactMissing)

  const contactOptions = useMemo(() => {
    const list = (contacts ?? []).map((c) => ({ id: c.id, name: c.name }))
    if (assignedContactId != null && !list.some((c) => c.id === assignedContactId)) {
      // Solange der Nachlade-Request laeuft (oder scheitert) wird die ID als
      // Platzhalter angezeigt — nie eine leere Auswahl, sonst wuerde ein
      // Speichern die Zuordnung unbemerkt entfernen.
      list.unshift(
        fetchedContact ?? { id: assignedContactId, name: t('form.contactUnknown', { id: assignedContactId }) },
      )
    }
    return list
  }, [contacts, assignedContactId, fetchedContact, t])

  // --- R6-Review F1: Waehrungsoptionen inkl. unbekannter Bestandswerte ---
  const extraCurrency = currency && !KNOWN_CURRENCIES.includes(currency) ? currency : null

  // --- R6-Review F2: Escape am Dialog + Verwerfen-Nachfrage ---
  const dialogRef = useRef<HTMLDivElement | null>(null)

  // Fokus in den Dialog holen, damit Escape auch ohne Feld-Fokus greift.
  useEffect(() => {
    dialogRef.current?.focus()
  }, [])

  const isDirty =
    contactId !== initialValues.contactId ||
    amount !== initialValues.amount ||
    time !== initialValues.time ||
    currency !== initialValues.currency ||
    currencyAmount !== initialValues.currencyAmount ||
    paymentMethod !== initialValues.paymentMethod ||
    memo !== initialValues.memo

  /** Schliessen-Wunsch: bei geaenderten Eingaben erst nachfragen. */
  function requestClose() {
    if (isPending) return
    if (isDirty) {
      setConfirmDiscard(true)
      return
    }
    onClose()
  }

  function validate(): boolean {
    const next: FormErrors = {}

    // contact_id ist beim Anlegen Pflicht; im Edit-Modus darf die Zuordnung
    // bewusst entfernt werden (ersetzt v1 select_contact/update_contact).
    if (mode === 'create' && !contactId) {
      next.contact = t('form.validation.contactRequired')
    }

    const parsed = Number(amount.replace(',', '.'))
    if (!amount.trim() || Number.isNaN(parsed)) {
      next.amount = t('form.validation.amountRequired')
    } else if (parsed <= 0) {
      next.amount = t('form.validation.amountPositive')
    }

    // currency ist Pflicht (Backend: 422 invalid_currency).
    if (!currency.trim()) {
      next.currency = t('form.validation.currencyRequired')
    }

    if (time) {
      const parsedTime = new Date(time)
      if (Number.isNaN(parsedTime.getTime())) {
        next.time = t('form.validation.timeInvalid')
      } else if (parsedTime.getTime() > Date.now() + 60_000) {
        next.time = t('form.validation.timeFuture')
      }
    }

    setErrors(next)
    return Object.keys(next).length === 0
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return

    const parsedCurrencyAmount = currencyAmount.trim()
      ? Number(currencyAmount.replace(',', '.'))
      : null

    const data: PaymentFormData = {
      amount: Math.abs(Number(amount.replace(',', '.'))),
      currency,
      currency_amount: Number.isNaN(parsedCurrencyAmount as number) ? null : parsedCurrencyAmount,
      contact_id: contactId ? Number(contactId) : null,
      payment_method: paymentMethod,
      memo: memo.trim() || null,
      time: time ? new Date(time).toISOString() : null,
    }

    try {
      if (mode === 'edit' && payment) {
        await updatePayment.mutateAsync({ id: payment.id, data })
        toast.success(t('form.toast.updated'))
      } else {
        await createPayment.mutateAsync(data)
        toast.success(t('form.toast.created'))
      }
      onSaved?.()
      onClose()
    } catch (err) {
      // Backend liefert englischen Klartext — wir uebersetzen nur den Code.
      toast.error(t(`errors.${paymentErrorCode(err)}`))
    }
  }

  const titleId = 'payment-form-title'

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      ref={dialogRef}
      tabIndex={-1}
      /* R6-Review F2: Escape wird AM DIALOG behandelt (nicht auf document) —
         sonst schliesst jedes Escape irgendwo in der App das Formular. Bei
         geaenderten Eingaben fragen wir vorher nach. */
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation()
          if (confirmDiscard) { setConfirmDiscard(false); return }
          requestClose()
        }
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) requestClose()
      }}
      data-testid="payment-form-modal"
      style={{ alignItems: 'flex-start', paddingTop: 32, outline: 'none' }}
    >
      <div className="modal" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h2 id={titleId}>{mode === 'edit' ? t('form.title.edit') : t('form.title.create')}</h2>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            aria-label={t('form.closeAriaLabel')}
            onClick={requestClose}
            data-testid="payment-form-close"
          >
            &times;
          </button>
        </div>

        {confirmDiscard && (
          <div className="modal-body" data-testid="payment-form-discard-confirm" role="alertdialog" aria-label={t('form.discard.title')}>
            <p>{t('form.discard.message')}</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setConfirmDiscard(false)}
                data-testid="payment-form-discard-cancel"
              >
                {t('form.discard.keepEditing')}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={onClose}
                data-testid="payment-form-discard-confirm-btn"
              >
                {t('form.discard.confirm')}
              </button>
            </div>
          </div>
        )}

        <form
          onSubmit={(e) => { void handleSubmit(e) }}
          style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}
        >
          <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
            <div className="form-group">
              <label htmlFor="payment-contact">
                {t('form.fields.contact')}
                {mode === 'create' && <span aria-hidden="true"> *</span>}
              </label>
              <select
                id="payment-contact"
                className={`input${errors.contact ? ' is-error' : ''}`}
                value={contactId}
                onChange={(e) => setContactId(e.target.value)}
                aria-required={mode === 'create'}
                aria-describedby={errors.contact ? 'payment-contact-error' : undefined}
                data-testid="payment-contact-select"
              >
                <option value="">
                  {mode === 'edit' ? t('form.contactNone') : t('form.contactPlaceholder')}
                </option>
                {contactOptions.map((c) => (
                  <option key={c.id} value={String(c.id)}>{c.name}</option>
                ))}
              </select>
              {mode === 'edit' && (
                <span className="form-hint muted">{t('form.hints.contactRemovable')}</span>
              )}
              {errors.contact && (
                <span id="payment-contact-error" className="field-error" role="alert">
                  {errors.contact}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="payment-amount">
                {t('form.fields.amount')} <span aria-hidden="true">*</span>
              </label>
              <input
                id="payment-amount"
                className={`input${errors.amount ? ' is-error' : ''}`}
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.0000"
                aria-required="true"
                aria-describedby={errors.amount ? 'payment-amount-error' : undefined}
                data-testid="payment-amount-input"
              />
              {errors.amount && (
                <span id="payment-amount-error" className="field-error" role="alert">
                  {errors.amount}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="payment-time">{t('form.fields.time')}</label>
              <input
                id="payment-time"
                className={`input${errors.time ? ' is-error' : ''}`}
                type="datetime-local"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                aria-describedby={errors.time ? 'payment-time-error' : undefined}
                data-testid="payment-time-input"
              />
              {errors.time && (
                <span id="payment-time-error" className="field-error" role="alert">
                  {errors.time}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="payment-currency">
                {t('form.fields.currency')} <span aria-hidden="true">*</span>
              </label>
              <select
                id="payment-currency"
                className={`input${errors.currency ? ' is-error' : ''}`}
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                aria-required="true"
                aria-describedby={errors.currency ? 'payment-currency-error' : undefined}
                data-testid="payment-currency-select"
              >
                {/* F-R6b-1: leere Pflichtauswahl — keine stille hnt-Vorbelegung. */}
                <option value="" disabled>
                  {t('form.currencyPlaceholder')}
                </option>
                {/* R6-Review F1: unbekannter Bestandswert (aelteres Backend, neue
                    Waehrung) bleibt waehlbar — sonst waere das controlled select
                    leer und wuerde die Waehrung beim Speichern ueberschreiben. */}
                {extraCurrency && (
                  <option value={extraCurrency}>{extraCurrency.toUpperCase()}</option>
                )}
                {CRYPTO_CURRENCIES.map((c) => (
                  <option key={c} value={c}>{t(`form.currencies.${c}`)}</option>
                ))}
                {FIAT_CURRENCIES.map((c) => (
                  <option key={c} value={c}>{c.toUpperCase()}</option>
                ))}
              </select>
              <span className="form-hint muted">{t('form.hints.fiatPayout')}</span>
              {errors.currency && (
                <span id="payment-currency-error" className="field-error" role="alert">
                  {errors.currency}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="payment-currency-amount">{t('form.fields.currencyAmount')}</label>
              <input
                id="payment-currency-amount"
                className="input"
                type="text"
                inputMode="decimal"
                value={currencyAmount}
                onChange={(e) => setCurrencyAmount(e.target.value)}
                data-testid="payment-currency-amount-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="payment-method">{t('form.fields.paymentMethod')}</label>
              <select
                id="payment-method"
                className="input"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                data-testid="payment-method-select"
              >
                {PAYMENT_METHODS.map((m) => (
                  <option key={m} value={m}>{t(`methods.${m}`)}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="payment-memo">{t('form.fields.memo')}</label>
              <textarea
                id="payment-memo"
                className="input"
                rows={3}
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                data-testid="payment-memo-input"
              />
            </div>
          </div>

          <div className="modal-footer" style={{ position: 'sticky', bottom: 0 }}>
            <button type="button" className="btn btn-ghost" onClick={requestClose} disabled={isPending}>
              {t('form.cancelBtn')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isPending}
              data-testid="payment-form-submit"
            >
              {isPending
                ? t('form.submitPending')
                : mode === 'edit'
                  ? t('form.submitEdit')
                  : t('form.submitCreate')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
